import { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2, StatusBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ListChecks, Plus, ShieldWarning, Camera, Ruler, DownloadSimple, UploadSimple, BookOpen, CaretUp, CaretDown, PencilSimple, MagnifyingGlass, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const PRIOS = ["niedrig", "mittel", "hoch", "kritisch"];
const MW_TYPES = ["zahl", "text", "boolean", "auswahl"];
const emptyForm = { target_type: "komponente", komponenten_kind: "", anlagentyp_key: "", code: "", gruppe: "", kategorie: "", beschreibung: "", norm: "", prioritaet: "mittel", sort: 0, pflichtfeld: true, sicherheitsrelevant: false, foto_erforderlich: false, messwert_erforderlich: false, messwert_typ: "zahl", messwert_einheit: "", messwert_min: "", messwert_max: "" };

function CheckpointDialog({ open, onOpenChange, initial, kinds, assetTypes, onSaved }) {
  const [f, setF] = useState(emptyForm);
  useEffect(() => {
    if (!open) return;
    if (initial) {
      const mw = initial.messwert || {};
      setF({ ...emptyForm, ...initial, target_type: initial.anlagentyp_key ? "anlagentyp" : "komponente",
        komponenten_kind: initial.komponenten_kind || "", anlagentyp_key: initial.anlagentyp_key || "",
        code: initial.code || "", gruppe: initial.gruppe || "", norm: initial.norm || "", sort: initial.sort || 0,
        messwert_erforderlich: !!initial.messwert_erforderlich, messwert_typ: mw.typ || "zahl",
        messwert_einheit: mw.einheit || "", messwert_min: mw.min ?? "", messwert_max: mw.max ?? "" });
    } else setF(emptyForm);
  }, [initial, open]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const valid = f.beschreibung && f.kategorie && (f.target_type === "komponente" ? f.komponenten_kind : f.anlagentyp_key);

  const save = async () => {
    if (!valid) return;
    const payload = {
      kategorie: f.kategorie, gruppe: f.gruppe || null, beschreibung: f.beschreibung, norm: f.norm || null,
      code: f.code || null, prioritaet: f.prioritaet, sort: Number(f.sort) || 0,
      pflichtfeld: f.pflichtfeld, sicherheitsrelevant: f.sicherheitsrelevant, foto_erforderlich: f.foto_erforderlich,
      messwert_erforderlich: f.messwert_erforderlich,
      komponenten_kind: f.target_type === "komponente" ? f.komponenten_kind : null,
      anlagentyp_key: f.target_type === "anlagentyp" ? f.anlagentyp_key : null,
      messwert: f.messwert_erforderlich ? { typ: f.messwert_typ, einheit: f.messwert_einheit || null,
        min: f.messwert_min !== "" ? Number(f.messwert_min) : null, max: f.messwert_max !== "" ? Number(f.messwert_max) : null, optionen: [] } : null,
    };
    try {
      if (initial?.id) await api.put(`/v1/checkpoints/${initial.id}`, payload);
      else await api.post(`/v1/checkpoints`, payload);
      toast.success(initial?.id ? "Neue Version gespeichert" : "Prüfpunkt angelegt");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cp-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Prüfpunkt · neue Version" : "Prüfpunkt anlegen"}</DialogTitle></DialogHeader>
        <DialogDescription>Versionierter Baustein des Prüfkatalogs. Zuordnung zu Komponententyp oder Anlagentyp.</DialogDescription>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label2>Ziel-Typ</Label2>
              <Select value={f.target_type} onValueChange={(v) => set("target_type", v)} disabled={!!initial?.id}>
                <SelectTrigger data-testid="cp-target-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="komponente">Komponententyp</SelectItem><SelectItem value="anlagentyp">Anlagentyp</SelectItem></SelectContent>
              </Select>
            </div>
            {f.target_type === "komponente" ? (
              <div><Label2>Komponententyp</Label2>
                <Select value={f.komponenten_kind} onValueChange={(v) => set("komponenten_kind", v)}>
                  <SelectTrigger data-testid="cp-kind"><SelectValue placeholder="Wählen…" /></SelectTrigger>
                  <SelectContent>{kinds.map((k) => <SelectItem key={k.key} value={k.key}>{k.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div><Label2>Anlagentyp</Label2>
                <Select value={f.anlagentyp_key} onValueChange={(v) => set("anlagentyp_key", v)}>
                  <SelectTrigger data-testid="cp-anlagentyp"><SelectValue placeholder="Wählen…" /></SelectTrigger>
                  <SelectContent>{assetTypes.map((a) => <SelectItem key={a.key} value={a.key}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label2>Code (Idempotenz)</Label2><Input data-testid="cp-code" placeholder="z. B. SEK-010" value={f.code} onChange={(e) => set("code", e.target.value)} /></div>
            <div><Label2>Gruppe</Label2><Input data-testid="cp-gruppe" placeholder="z. B. Sicherheit" value={f.gruppe} onChange={(e) => set("gruppe", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label2>Kategorie *</Label2><Input data-testid="cp-kategorie" value={f.kategorie} onChange={(e) => set("kategorie", e.target.value)} /></div>
            <div><Label2>Reihenfolge (sort)</Label2><Input data-testid="cp-sort" type="number" value={f.sort} onChange={(e) => set("sort", e.target.value)} /></div>
          </div>
          <div><Label2>Beschreibung *</Label2><Input data-testid="cp-beschreibung" value={f.beschreibung} onChange={(e) => set("beschreibung", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label2>Norm</Label2><Input data-testid="cp-norm" placeholder="z. B. EN 13241" value={f.norm} onChange={(e) => set("norm", e.target.value)} /></div>
            <div><Label2>Priorität</Label2>
              <Select value={f.prioritaet} onValueChange={(v) => set("prioritaet", v)}>
                <SelectTrigger data-testid="cp-prio"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIOS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2 rounded-md border border-border p-3">
            <div className="flex items-center justify-between text-sm"><span>Pflichtfeld</span><Switch checked={f.pflichtfeld} onCheckedChange={(v) => set("pflichtfeld", v)} data-testid="cp-pflicht" /></div>
            <div className="flex items-center justify-between text-sm"><span>Sicherheitsrelevant</span><Switch checked={f.sicherheitsrelevant} onCheckedChange={(v) => set("sicherheitsrelevant", v)} data-testid="cp-safety" /></div>
            <div className="flex items-center justify-between text-sm"><span>Foto erforderlich</span><Switch checked={f.foto_erforderlich} onCheckedChange={(v) => set("foto_erforderlich", v)} data-testid="cp-foto" /></div>
            <div className="flex items-center justify-between text-sm"><span>Messwert erforderlich</span><Switch checked={f.messwert_erforderlich} onCheckedChange={(v) => set("messwert_erforderlich", v)} data-testid="cp-messwert" /></div>
          </div>
          {f.messwert_erforderlich && (
            <div className="grid grid-cols-4 gap-2 rounded-md border border-border p-3">
              <div className="col-span-2"><Label2>Messwert-Typ</Label2>
                <Select value={f.messwert_typ} onValueChange={(v) => set("messwert_typ", v)}>
                  <SelectTrigger data-testid="cp-mw-typ"><SelectValue /></SelectTrigger>
                  <SelectContent>{MW_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label2>Einheit</Label2><Input placeholder="N" value={f.messwert_einheit} onChange={(e) => set("messwert_einheit", e.target.value)} /></div>
              <div />
              <div><Label2>Min</Label2><Input type="number" value={f.messwert_min} onChange={(e) => set("messwert_min", e.target.value)} /></div>
              <div><Label2>Max</Label2><Input type="number" value={f.messwert_max} onChange={(e) => set("messwert_max", e.target.value)} /></div>
            </div>
          )}
        </div>
        <DialogFooter><Button data-testid="cp-save" onClick={save} disabled={!valid}>{initial?.id ? "Neue Version" : "Anlegen"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CheckpointRow({ c, canEdit, onEdit, onDeactivate, onMove, isFirst, isLast }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm" data-testid={`cp-row-${c.code || c.id}`}>
      <div className="flex min-w-0 items-center gap-2">
        <span className="w-8 shrink-0 text-xs text-muted-foreground">{c.sort ?? 0}</span>
        {c.sicherheitsrelevant && <ShieldWarning size={16} weight="fill" className="shrink-0 text-red-500" title="Sicherheitsrelevant" />}
        {c.foto_erforderlich && <Camera size={15} weight="duotone" className="shrink-0 text-primary" title="Foto erforderlich" />}
        {c.messwert_erforderlich && <Ruler size={15} weight="duotone" className="shrink-0 text-amber-600" title="Messwert erforderlich" />}
        <span className="truncate">{c.beschreibung}</span>
        <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">· {c.gruppe || c.kategorie}{c.norm ? ` · ${c.norm}` : ""}{c.code ? ` · ${c.code}` : ""}</span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {!c.pflichtfeld && <Badge variant="outline" className="rounded-md text-[10px]">optional</Badge>}
        <Badge variant="outline" className="rounded-md text-xs">v{c.version}</Badge>
        {canEdit && <>
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={isFirst} onClick={() => onMove(c, -1)} data-testid={`cp-up-${c.code || c.id}`}><CaretUp size={13} /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={isLast} onClick={() => onMove(c, 1)} data-testid={`cp-down-${c.code || c.id}`}><CaretDown size={13} /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(c)} data-testid={`cp-edit-${c.code || c.id}`}><PencilSimple size={13} /></Button>
        </>}
      </div>
    </div>
  );
}

const PRIO_W = { niedrig: 3, mittel: 8, hoch: 15, kritisch: 30 };
const PRIO_ORDER = ["niedrig", "mittel", "hoch", "kritisch"];
const PRIO_COLOR = { niedrig: "bg-slate-400", mittel: "bg-blue-500", hoch: "bg-amber-500", kritisch: "bg-red-500" };

// Qualitätsübersicht je Katalog – ausschließlich für Admins (QS beim Aufbau der Standardbibliothek).
function CatalogQuality({ list }) {
  const total = list.length || 1;
  const stats = {
    Prüfpunkte: list.length,
    Pflicht: list.filter((c) => c.pflichtfeld).length,
    Optional: list.filter((c) => !c.pflichtfeld).length,
    Sicherheit: list.filter((c) => c.sicherheitsrelevant).length,
    Foto: list.filter((c) => c.foto_erforderlich).length,
    Messwert: list.filter((c) => c.messwert_erforderlich).length,
  };
  const dist = { niedrig: 0, mittel: 0, hoch: 0, kritisch: 0 };
  let health = 0;
  list.forEach((c) => {
    const p = c.prioritaet || "mittel";
    if (dist[p] != null) dist[p] += 1;
    health += c.health_gewicht != null ? c.health_gewicht : (PRIO_W[p] ?? 8);
  });
  return (
    <div className="border-b border-border bg-muted/30 px-4 py-2.5" data-testid="catalog-quality">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="mr-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">QS</span>
        {Object.entries(stats).map(([k, v]) => (
          <span key={k} className="text-muted-foreground" data-testid={`qs-${k.toLowerCase()}`}><span className="font-semibold text-foreground">{v}</span> {k}</span>
        ))}
        <span className="text-muted-foreground" data-testid="qs-health-sum"><span className="font-semibold text-foreground">{health}</span> Σ Health</span>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Health-Verteilung</span>
        <div className="flex h-2 flex-1 overflow-hidden rounded-full bg-muted">
          {PRIO_ORDER.map((p) => dist[p] > 0 && <div key={p} className={PRIO_COLOR[p]} style={{ width: `${(dist[p] / total) * 100}%` }} title={`${p}: ${dist[p]}`} />)}
        </div>
        <span className="text-[10px] text-muted-foreground">{PRIO_ORDER.map((p) => `${p.charAt(0).toUpperCase()}${dist[p]}`).join(" · ")}</span>
      </div>
    </div>
  );
}

function ImportDialog({ open, onOpenChange, onDone, nameFor }) {
  const [file, setFile] = useState(null);
  const [isCsv, setIsCsv] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => { if (!open) { setFile(null); setPreview(null); setError(null); setIsCsv(false); } }, [open]);

  const pick = (f) => { setFile(f); setIsCsv((f?.name || "").toLowerCase().endsWith(".csv")); setPreview(null); setError(null); };

  const run = async (dryRun) => {
    if (!file) return;
    const text = await file.text();
    let payload = null;
    if (!isCsv) { try { payload = JSON.parse(text); } catch { setError("Ungültige JSON-Datei (Syntaxfehler)."); setPreview(null); return; } }
    setBusy(true); setError(null);
    try {
      const qs = dryRun ? "?dry_run=true" : "";
      const url = isCsv ? `/v1/checkpoints/import-csv${qs}` : `/v1/checkpoints/import${qs}`;
      const body = isCsv ? { csv: text, mode: "upsert" } : payload;
      const { data } = await api.post(url, body);
      if (dryRun) { setPreview(data); }
      else {
        const c = (data.imported || []).reduce((a, r) => a + (r.created || 0), 0);
        const u = (data.imported || []).reduce((a, r) => a + (r.updated || 0), 0);
        toast.success("Import erfolgreich", { description: `${(data.imported || []).length} Katalog(e) · ${c} neu · ${u} aktualisiert` });
        onOpenChange(false); onDone();
      }
    } catch (e) { setPreview(null); setError(formatApiError(e.response?.data?.detail) || "Import fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const totalNew = preview ? preview.imported.reduce((a, r) => a + (r.created || 0), 0) : 0;
  const totalUpd = preview ? preview.imported.reduce((a, r) => a + (r.updated || 0), 0) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="import-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-heading">Prüfkataloge importieren</DialogTitle></DialogHeader>
        <DialogDescription>JSON oder CSV. Prüfe die Datei zuerst per Dry-Run – es wird nichts gespeichert. So siehst du vorab neu/aktualisiert je Katalog und etwaige Fehler.</DialogDescription>
        <div className="space-y-3">
          <input ref={ref} type="file" accept=".json,.csv,application/json,text/csv" hidden onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} data-testid="import-file-input" />
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-2" onClick={() => ref.current?.click()} data-testid="import-choose-file"><UploadSimple size={15} /> Datei wählen</Button>
            <span className="truncate text-sm text-muted-foreground" data-testid="import-filename">{file ? `${file.name} (${isCsv ? "CSV" : "JSON"})` : "Keine Datei gewählt"}</span>
          </div>

          {error && <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-600" data-testid="import-error"><WarningCircle size={15} weight="fill" className="mr-1 inline" />{error}</div>}

          {preview && (
            <div className="rounded-md border border-border" data-testid="import-preview">
              <div className="flex flex-wrap items-center gap-2 border-b border-border p-3 text-sm">
                <CheckCircle size={16} weight="fill" className="text-emerald-600" />
                <span className="font-semibold">Vorschau (Dry-Run) – nichts gespeichert</span>
                <Badge variant="outline" className="rounded-md border-emerald-500/40 bg-emerald-500/10 text-emerald-600" data-testid="preview-total-new">{totalNew} neu</Badge>
                <Badge variant="outline" className="rounded-md border-blue-500/40 bg-blue-500/10 text-blue-600" data-testid="preview-total-upd">{totalUpd} aktualisiert</Badge>
                <Badge variant="outline" className="rounded-md">{preview.imported.length} Katalog(e)</Badge>
              </div>
              <div className="divide-y divide-border">
                {preview.imported.map((r, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`import-cat-${r.target_key}`}>
                    <div className="min-w-0"><span className="font-medium">{nameFor(r.target_type, r.target_key)}</span> <span className="text-xs text-muted-foreground">({r.target_type} · {r.checkpoints ?? ((r.created || 0) + (r.updated || 0))} Prüfpunkte)</span></div>
                    <div className="flex shrink-0 gap-1.5 text-xs">
                      <Badge variant="outline" className="rounded-md border-emerald-500/40 text-emerald-600">+{r.created || 0} neu</Badge>
                      <Badge variant="outline" className="rounded-md border-blue-500/40 text-blue-600">~{r.updated || 0} akt.</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="gap-2" disabled={!file || busy} onClick={() => run(true)} data-testid="import-dryrun-btn"><MagnifyingGlass size={15} /> Datei prüfen (Dry-Run)</Button>
          <Button className="gap-2" disabled={!file || busy} onClick={() => run(false)} data-testid="import-commit-btn"><UploadSimple size={15} /> Importieren</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Pruefpunkte() {  const { user } = useAuth();
  const canEdit = ["admin", "buero"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState(null);
  const [kinds, setKinds] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [meta, setMeta] = useState([]);
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const [importing, setImporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/v1/checkpoints").then((r) => setItems(r.data));
    api.get("/v1/catalogs/component-types").then((r) => setKinds(r.data));
    api.get("/v1/catalogs/asset-types").then((r) => setAssetTypes(r.data));
    api.get("/v1/pruefkataloge").then((r) => setMeta(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const metaFor = (type, key) => meta.find((m) => m.target_type === type && m.target_key === key);

  const importStdLib = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/v1/checkpoints/import-standard-library");
      const total = (data.imported || []).reduce((a, r) => a + r.created + r.updated, 0);
      toast.success(`Standardbibliothek importiert · ${data.imported.length} Kataloge, ${total} Prüfpunkte${data.skipped?.length ? ` · ${data.skipped.length} übersprungen` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const exportCatalog = async (type, key) => {
    const param = type === "anlagentyp" ? `anlagentyp_key=${key}` : `kind=${key}`;
    try {
      const { data } = await api.get(`/v1/checkpoints/export?${param}`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
      a.download = `pruefkatalog_${key}.json`; a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const move = async (group, c, dir) => {
    const ordered = [...group].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
    const i = ordered.findIndex((x) => x.id === c.id);
    const j = i + dir;
    if (j < 0 || j >= ordered.length) return;
    [ordered[i], ordered[j]] = [ordered[j], ordered[i]];
    try { await api.put("/v1/checkpoints/reorder", { order: ordered.map((x) => x.id) }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!items) return <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>;

  const anlage = items.filter((c) => c.anlagentyp_key);
  const komp = items.filter((c) => !c.anlagentyp_key);
  const groupBy = (arr, field) => arr.reduce((m, c) => { const k = c[field] || "sonstiges"; (m[k] = m[k] || []).push(c); return m; }, {});
  const nameFor = (type, key) => (type === "anlagentyp" ? assetTypes : kinds).find((x) => x.key === key)?.name || key;

  const renderGroups = (arr, type, field) => {
    const groups = groupBy(arr, field);
    const keys = Object.keys(groups).sort();
    if (keys.length === 0) return <Card className="rounded-md border p-8 text-center text-sm text-muted-foreground">Noch keine Prüfpunkte. Importiere die Standardbibliothek oder lege welche an.</Card>;
    return keys.map((key) => {
      const list = groups[key].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
      const m = metaFor(type, key);
      return (
        <Card key={key} className="rounded-md border" data-testid={`cp-group-${key}`}>
          <div className="flex items-center gap-2 border-b border-border p-4">
            <ListChecks size={18} weight="duotone" className="text-primary" />
            <Label2>{nameFor(type, key)}</Label2>
            <Badge variant="outline" className="rounded-md">{list.length}</Badge>
            {m && <StatusBadge tone="blue">Katalog v{m.version}</StatusBadge>}
            <Button size="sm" variant="ghost" className="ml-auto h-7 gap-1.5 text-xs" onClick={() => exportCatalog(type, key)} data-testid={`cp-export-${key}`}><DownloadSimple size={14} /> Export</Button>
          </div>
          {isAdmin && <CatalogQuality list={list} />}
          <div className="divide-y divide-border">
            {list.map((c, idx) => (
              <CheckpointRow key={c.id} c={c} canEdit={canEdit} isFirst={idx === 0} isLast={idx === list.length - 1}
                onEdit={(cp) => setDlg({ open: true, initial: cp })} onMove={(cp, d) => move(list, cp, d)} />
            ))}
          </div>
        </Card>
      );
    });
  };

  return (
    <div>
      <PageHeader title="Prüfkatalog-Verwaltung" subtitle="No-Code Fundament aller Checklisten · versioniert · Basis für Wartungs-Prüfprotokolle">
        {canEdit && <>
          <Button size="sm" variant="outline" className="gap-2" onClick={importStdLib} disabled={importing} data-testid="cp-import-stdlib-btn"><BookOpen size={16} weight="duotone" /> {importing ? "Importiere…" : "Standardbibliothek importieren"}</Button>
          <Button size="sm" variant="outline" className="gap-2" onClick={() => setImportOpen(true)} data-testid="cp-import-btn"><UploadSimple size={16} /> Import / Vorschau</Button>
          <Button size="sm" className="gap-2" onClick={() => setDlg({ open: true, initial: null })} data-testid="cp-create-btn"><Plus size={16} weight="bold" /> Prüfpunkt</Button>
        </>}
      </PageHeader>

      <Tabs defaultValue="komponente">
        <TabsList data-testid="cp-tabs">
          <TabsTrigger value="komponente" data-testid="cp-tab-komponente">Komponententypen ({komp.length})</TabsTrigger>
          <TabsTrigger value="anlagentyp" data-testid="cp-tab-anlagentyp">Anlagentypen ({anlage.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="komponente" className="mt-4 space-y-4">{renderGroups(komp, "komponente", "komponenten_kind")}</TabsContent>
        <TabsContent value="anlagentyp" className="mt-4 space-y-4">{renderGroups(anlage, "anlagentyp", "anlagentyp_key")}</TabsContent>
      </Tabs>

      <CheckpointDialog open={dlg.open} onOpenChange={(o) => setDlg({ ...dlg, open: o })}
        initial={dlg.initial} kinds={kinds} assetTypes={assetTypes} onSaved={load} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} nameFor={nameFor} />
    </div>
  );
}
